import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Engineering as EngineeringIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface WorkflowTemplate {
  id: number;
  name: string;
  template_type: string;
  description: string;
  is_default?: boolean;
}

interface WorkflowTemplateSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: number) => void;
  jobId: number;
}

const WorkflowTemplateSelector: React.FC<WorkflowTemplateSelectorProps> = ({
  open,
  onClose,
  onSelect,
  jobId
}) => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/workflows/templates');
      setTemplates(response.data);
      
      // Auto-select default template or if only one template
      const defaultTemplate = response.data.find((t: WorkflowTemplate) => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
      } else if (response.data.length === 1) {
        setSelectedTemplate(response.data[0].id);
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load workflow templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    try {
      setSubmitting(true);
      await api.post(`/workflows/jobs/${jobId}/workflow`, {
        template_id: selectedTemplate
      });
      
      onSelect(selectedTemplate);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign workflow');
    } finally {
      setSubmitting(false);
    }
  };

  const getTemplateIcon = (templateType: string) => {
    switch (templateType) {
      case 'technical':
        return <EngineeringIcon />;
      case 'temp_to_hire':
        return <ScheduleIcon />;
      case 'wm_i7m_sop':
        return <DescriptionIcon />;
      case 'standard':
      default:
        return <BusinessIcon />;
    }
  };

  const getTemplateColor = (templateType: string): "default" | "primary" | "secondary" => {
    switch (templateType) {
      case 'technical':
        return 'secondary';
      case 'temp_to_hire':
        return 'primary';
      case 'standard':
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          Select Workflow Template
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a workflow template that best matches your hiring process for this position.
            </Typography>
            
            <RadioGroup
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(Number(e.target.value))}
            >
              {templates.map((template) => (
                <Card 
                  key={template.id} 
                  sx={{ 
                    mb: 2, 
                    border: selectedTemplate === template.id ? 2 : 1,
                    borderColor: selectedTemplate === template.id ? 'primary.main' : 'divider',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedTemplate(template.id)}
                >
                  <CardContent>
                    <FormControlLabel
                      value={template.id}
                      control={<Radio />}
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {getTemplateIcon(template.template_type)}
                            <Typography variant="subtitle1" fontWeight="medium">
                              {template.name}
                            </Typography>
                            {template.is_default && (
                              <Chip 
                                label="Default"
                                size="small"
                                color="primary"
                              />
                            )}
                            <Chip 
                              label={template.template_type.replace(/_/g, ' ')}
                              size="small"
                              color={getTemplateColor(template.template_type)}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                You can change the workflow template later if needed. Each template includes 
                predefined steps that can be customized for your specific needs.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!selectedTemplate || submitting}
        >
          {submitting ? 'Assigning...' : 'Assign Workflow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowTemplateSelector;